import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText } from 'lucide-react'
import { billingApi } from '@/api/billing'
import { Badge, Button, Loading, Modal } from '@/components/ui'
import { formatNIS } from '@/utils/currency'
import { formatDate, formatDateTime } from '@/utils/date'
import type { BillingCharge, LineItem } from '@/types/billing'

interface Props {
  orgId: string
}

function chargeStatusVariant(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'succeeded':
      return 'success'
    case 'pending':
      return 'warning'
    case 'failed':
      return 'danger'
    default:
      return 'neutral'
  }
}

function LineItemsBreakdown({ items }: { items: LineItem[] }) {
  if (!items.length) return <p className="text-sm text-ink-secondary">No line items stored on this charge.</p>
  return (
    <ul className="space-y-2">
      {items.map((li, i) => (
        <li
          key={i}
          className="flex flex-wrap justify-between gap-2 text-sm border-b border-navy-border/60 pb-2 last:border-0"
        >
          <span className="text-ink-secondary">
            <span className="uppercase text-xs text-ink-muted mr-2">{li.type}</span>
            {'label' in li ? li.label : '—'}
          </span>
          <span className="num font-medium text-ink-primary">{formatNIS(li.amount_agorot)}</span>
        </li>
      ))}
    </ul>
  )
}

export default function ChargeHistoryTable({ orgId }: Props) {
  const [open, setOpen] = useState<BillingCharge | null>(null)
  const { data: charges, isLoading } = useQuery({
    queryKey: ['admin-billing-charges', orgId],
    queryFn: () => billingApi.getCharges(orgId, 20),
    staleTime: 60_000,
  })

  return (
    <>
      <div className="card p-6 border border-navy-border bg-[#1a1d27]">
        <h3 className="text-sm font-semibold text-ink-primary mb-4">Charge history</h3>
        {isLoading ? (
          <Loading text="Loading charges…" />
        ) : !charges?.length ? (
          <p className="text-sm text-ink-secondary">No charges yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-muted border-b border-navy-border">
                  <th className="pb-2 pr-4 font-medium">Created</th>
                  <th className="pb-2 pr-4 font-medium">Period</th>
                  <th className="pb-2 pr-4 font-medium">Amount</th>
                  <th className="pb-2 pr-4 font-medium">Status</th>
                  <th className="pb-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {charges.map((c) => (
                  <tr key={c.id} className="border-b border-navy-border/60 last:border-0">
                    <td className="py-2 pr-4 text-ink-secondary whitespace-nowrap">
                      {formatDateTime(c.created_at)}
                    </td>
                    <td className="py-2 pr-4 text-ink-secondary num whitespace-nowrap">
                      {formatDate(c.period_start)} – {formatDate(c.period_end)}
                    </td>
                    <td className="py-2 pr-4 text-ink-primary font-medium num">{formatNIS(c.amount_agorot)}</td>
                    <td className="py-2 pr-4">
                      <Badge variant={chargeStatusVariant(c.status)}>{c.status}</Badge>
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        {c.sumit_invoice_url && (
                          <a
                            href={c.sumit_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-indigo-400 hover:text-indigo-300 text-xs"
                          >
                            <FileText className="h-3.5 w-3.5" />
                            View PDF
                          </a>
                        )}
                        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(c)}>
                          Breakdown
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={!!open}
        onClose={() => setOpen(null)}
        title={open ? `Charge breakdown · ${formatNIS(open.amount_agorot)}` : undefined}
        size="lg"
      >
        {open && (
          <div className="space-y-4">
            <p className="text-xs text-ink-muted">
              Status <span className="text-ink-secondary">{open.status}</span>
              {open.charged_at && (
                <>
                  {' '}
                  · Charged {formatDateTime(open.charged_at)}
                </>
              )}
            </p>
            {open.failure_reason && (
              <p className="text-sm text-red-400">Failure: {open.failure_reason}</p>
            )}
            <LineItemsBreakdown items={open.line_items} />
          </div>
        )}
      </Modal>
    </>
  )
}
